import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Axios from "axios";
import cn from "classnames";
import { cloneDeep, isEqual, template } from "lodash";
import { useImmer } from "use-immer";
import {
    Checkbox,
    Form,
    Button,
    Message,
    Divider,
    FormInstance,
    toaster,
    Schema,
    Heading,
} from "rsuite";

import useDisabledFeatureSettingNames from "hooks/useDisabledFeatureSettingNames";
import usePreviousState from "hooks/usePreviousState";
import useRefreshPage from "hooks/useRefreshPage";
import useRequestCancellation from "hooks/useRequestCancellation";

import constants from "utils/constants";
import { getFileExtension } from "utils/file-helper";
import { convertNumericStringProperties } from "utils/numeric-strings-convert-helper";
import { validateContent } from "utils/regex-helper";
import { tenantFeatureSettingDefinitions } from "utils/tenant-workflows/tenant-workflows-definitions";
import {
    isEverySettingWorkFlowUpdated,
    isEveryWorkflowStepCompleted,
    isTenantFeatureSettingWithTypeEnabled,
    updateTenantSettingWorkFlow,
} from "utils/tenant-workflows/tenant-workflows-helper";
import {
    ITenantFeatureSettingWorkFlow,
    TenantFeatureSettingWorkFlowToggleValue,
    TenantFeatureSettingWorkFlowType,
    WorkflowModalType,
    WorkflowStatus,
} from "utils/tenant-workflows/tenant-workflows-types";
import { setFileName } from "utils/url-helper";

import { getErrorMessage } from "api/defaults";
import {
    loadInvoiceAdditionalHeaderInformationField,
    updateInvoiceAdditionalHeaderInformationField,
} from "api/environments/tenants/invoices/invoice-additional-header-information";
import { uploadLogo } from "api/environments/tenants/logo-api";
import {
    loadOcrConfigurations,
    updateOcrConfigurations,
} from "api/environments/tenants/ocr-configurations-api";
import {
    loadSearchServices,
    loadSearchServiceLinked,
    recreateSearchIndex,
    updateSearchIndex,
} from "api/environments/tenants/search-services";
import {
    loadFeatureConfigurations,
    updateFeatureConfigurations,
    checkTenantDatabaseAlertRuleExistence,
} from "api/environments/tenants/tenant-feature-configurations-api";
import {
    getTenantFeatureSettingWorkFlows,
    toggleTenantFeatureSetting,
    toggleTenantFeatureSettings,
} from "api/environments/tenants/tenant-feature-settings-api";
import {
    loadSftpUserIpAddresses,
    updateSftpUserIpAddresses,
} from "api/environments/tenants/tenant-sftp-settings-api";
import {
    reIndexVectorizationData,
    seedVectorizationData,
} from "api/environments/tenants/vendor-similarity-search-api";
import {
    loadConfluenceDocumentLinks,
    loadMicrosoftDocumentLinks,
} from "api/information-links-api";

import { IAppSettings } from "store/app-settings/app-settings-types";
import { CustomerType } from "store/customers/add-new-customer/add-new-customer-types";
import { IInvoiceAdditionalHeaderInformation } from "store/environments/tenants/invoices/invoice-additional-header-information/invoice-additional-header-information-types";
import {
    IAllowedIpAddress,
    ISearchService,
    ISftpUserWithIpAddresses,
    Features,
    OcrConfiguration,
    OcrMethods,
    TenantFeatureGroup,
} from "store/environments/tenants/tenant-configurations/tenant-configurations-types";
import {
    ConfluenceLinks,
    MicrosoftLinks,
} from "store/information-links/information-links-types";

import ConfirmModal from "components/Common/ConfirmModal/ConfirmModal";
import ErrorSection from "components/Common/ErrorSection/ErrorSection";
import Footer from "components/Common/Footer/Footer";
import ProcessModalButton from "components/WorkflowLog/ProcessModalButton/ProcessModalButton";
import SignalRTenantFeatureSetting from "components/WorkflowLog/TenantFeature/SignalRTenantFeatureSetting";

import FeatureGroups from "./FeatureGroups/FeatureGroups";
import Logo from "./Logo/Logo";

import "./FeatureConfigurations.less";

interface ComponentProps {
    envMoniker: string;
    tenantMoniker: string;
    tenantId: string;
    tenantName: string;
    customerType: CustomerType;
    isPowerBiWorkspaceDeploymentAllowed: boolean;
    appSettings: IAppSettings;
    setIsUnsavedChangeAvailable: (isUnsavedChangeAvailable: boolean) => void;
    showLoader: (isVisible: boolean) => void;
}

const defaultSftpUser: ISftpUserWithIpAddresses = {
    id: 0,
    allowedIpAddresses: [],
};

const FeatureConfigurations: React.FC<ComponentProps> = ({
    envMoniker,
    tenantMoniker,
    tenantName,
    customerType,
    isPowerBiWorkspaceDeploymentAllowed,
    appSettings,
    setIsUnsavedChangeAvailable,
    showLoader,
}: ComponentProps) => {
    const { t } = useTranslation();
    const cancelTokenSource = useRequestCancellation();
    const formRef = useRef<FormInstance<Record<string, any>> | null>(null);
    const environmentId =
        useParams<{ environmentId: string }>().environmentId ?? "";
    const tenantId = useParams<{ tenantId: string }>().tenantId ?? "";

    const [
        previousFeatureSettingWorkFlows,
        featureSettingWorkFlows,
        setFeatureSettingWorkFlows,
    ] = usePreviousState<ITenantFeatureSettingWorkFlow[] | null>(null);

    const [
        disabledSettingNames,
        addDisabledSettingNames,
        removeDisabledSettingName,
    ] = useDisabledFeatureSettingNames();

    const [isDataFetched, setDataFetched] = useState<boolean>(false);

    const [isSettingsFormValid, setSettingsFormValid] = useState<boolean>(true);
    const [features, setFeatures] = useImmer<Features | null>(null);
    const originalFeatures = useRef<Features | null>(null);
    const [featuresErrorMessage, setFeaturesErrorMessage] =
        useState<string>("");
    const originalOcrConfiguration = useRef<OcrConfiguration>();
    const [ocrConfiguration, setOcrConfiguration] =
        useState<OcrConfiguration>();
    const originalWorkflowFromXmlField =
        useRef<IInvoiceAdditionalHeaderInformation | null>(null);
    const [workflowFromXmlField, setWorkflowFromXmlField] =
        useState<IInvoiceAdditionalHeaderInformation | null>(null);

    const [confluenceDocumentLinks, setConfluenceDocumentLinks] =
        useState<ConfluenceLinks | null>(null);
    const [microsoftDocumentLinks, setMicrosoftDocumentLinks] =
        useState<MicrosoftLinks | null>(null);
    const [alertRuleExists, setAlertRuleExists] = useState<boolean | null>(
        null
    );

    const [isModifySearchDisabled, setModifySearchDisabled] =
        useState<boolean>(false);
    const [searchConfirmVisible, setSearchConfirmVisible] = useState(false);

    const [logoName, setLogoName] = useState<string>("");

    const codingPredictionWorkflow = featureSettingWorkFlows?.find(
        (workflow) =>
            workflow.type === TenantFeatureSettingWorkFlowType.CodingPrediction
    );

    const originalSftpUserWithIpAddresses =
        useRef<ISftpUserWithIpAddresses>(defaultSftpUser);
    const [sftpUserWithIpAddresses, setSftpUserWithIpAddresses] =
        useState<ISftpUserWithIpAddresses>(defaultSftpUser);
    const [areSftpAddressesValid, setSftpAddressesValid] =
        useState<boolean>(true);

    const [searchServices, setSearchServices] = useState<ISearchService[]>([]);
    const [linkedSearchService, setLinkedSearchService] = useState<
        string | null
    >(null);
    const [originalSearchService, setOriginalSearchService] = useState<
        string | null
    >(null);

    const title = t("tenantConfig.featureConfigurations.header");

    const isMlSalesTaxDisabled =
        !features?.codingPredictionEnabled ||
        !codingPredictionWorkflow ||
        !(
            codingPredictionWorkflow?.status === WorkflowStatus.Success &&
            codingPredictionWorkflow.toggleValue
        );

    const powerBiWorkspaceDeploymentWorkflow = featureSettingWorkFlows?.find(
        (workflow) =>
            workflow.type ===
            TenantFeatureSettingWorkFlowType.PowerBiWorkspaceDeployment
    );

    const searchServiceSelectionWorkflow = featureSettingWorkFlows?.find(
        (workflow) =>
            workflow.type ===
            TenantFeatureSettingWorkFlowType.SearchServiceSelection
    );

    const alertRuleDeploymentWorkflow = featureSettingWorkFlows?.find(
        (workflow) =>
            workflow.type ===
            TenantFeatureSettingWorkFlowType.DatabaseAlertRuleCreation
    );

    const enableSftpWorkflow = featureSettingWorkFlows?.find(
        (workflow) =>
            workflow.type === TenantFeatureSettingWorkFlowType.EnableSftp
    );

    const enableSftpToSettingMapping = {
        workflowType: TenantFeatureSettingWorkFlowType.EnableSftp,
        getFeature: (feature: Features) => feature.sftpEnabled,
        text: t("tenantConfig.featureConfigurations.sftpEnabled.title"),
        helpText: t(
            "tenantConfig.featureConfigurations.sftpEnabled.description"
        ),
        value: "sftpEnabled",
        checked: features?.sftpEnabled,
        isDisabled:
            (!sftpUserWithIpAddresses.allowedIpAddresses.length &&
                !features?.sftpEnabled) ||
            (!areSftpAddressesValid && !features?.sftpEnabled),
        isAvailableForAx: true,
        group: TenantFeatureGroup.sftp,
    };

    const workFlowTypeToSettingMapping = [
        {
            workflowType:
                TenantFeatureSettingWorkFlowType.D365RoleDooapRequiredToAccessDooap,
            getFeature: (feature: Features) =>
                feature.d365RoleDooapRequiredToAccessDooap,
            text: t(
                "tenantConfig.featureConfigurations.d365RoleDooapRequiredToAccessDooap.title"
            ),
            helpText: t(
                "tenantConfig.featureConfigurations.d365RoleDooapRequiredToAccessDooap.description"
            ),
            value: "d365RoleDooapRequiredToAccessDooap",
            checked: features?.d365RoleDooapRequiredToAccessDooap,
            isDisabled: false,
            isAvailableForAx: false,
            group: TenantFeatureGroup.other,
        },
        {
            workflowType: TenantFeatureSettingWorkFlowType.DocumentsTransfer,
            getFeature: (feature: Features) =>
                feature.transferInvoiceDocumentsToAxDuringTransferToPayment,
            text: t(
                "tenantConfig.featureConfigurations.transferInvoiceDocuments.title"
            ),
            helpText: t(
                "tenantConfig.featureConfigurations.transferInvoiceDocuments.description"
            ),
            value: "transferInvoiceDocumentsToAxDuringTransferToPayment",
            checked:
                features?.transferInvoiceDocumentsToAxDuringTransferToPayment,
            isDisabled: false,
            isAvailableForAx: false,
            group: TenantFeatureGroup.other,
        },
        {
            workflowType: TenantFeatureSettingWorkFlowType.CodingPrediction,
            getFeature: (feature: Features) => feature.codingPredictionEnabled,
            text: t(
                "tenantConfig.featureConfigurations.codingPrediction.title"
            ),
            helpText: t(
                "tenantConfig.featureConfigurations.codingPrediction.description"
            ),
            value: "codingPredictionEnabled",
            checked: features?.codingPredictionEnabled,
            isDisabled: false,
            isAvailableForAx: true,
            group: TenantFeatureGroup.mlCoding,
        },
        {
            workflowType: TenantFeatureSettingWorkFlowType.WorkflowPrediction,
            getFeature: (feature: Features) =>
                feature.workflowPredictionEnabled,
            text: t(
                "tenantConfig.featureConfigurations.workflowPrediction.title"
            ),
            helpText: t(
                "tenantConfig.featureConfigurations.workflowPrediction.description"
            ),
            value: "workflowPredictionEnabled",
            checked: features?.workflowPredictionEnabled,
            isDisabled: false,
            isAvailableForAx: true,
            group: TenantFeatureGroup.mlWorkflow,
        },
        {
            workflowType: TenantFeatureSettingWorkFlowType.SalesTaxPrediction,
            getFeature: (feature: Features) =>
                feature.salesTaxPredictionEnabled,
            text: t(
                "tenantConfig.featureConfigurations.salesTaxPrediction.title"
            ),
            helpText: t(
                "tenantConfig.featureConfigurations.salesTaxPrediction.description"
            ),
            value: "salesTaxPredictionEnabled",
            checked: features?.salesTaxPredictionEnabled,
            isDisabled: isMlSalesTaxDisabled,
            isAvailableForAx: true,
            group: TenantFeatureGroup.mlCoding,
        },
        {
            workflowType:
                TenantFeatureSettingWorkFlowType.SimilarityVendorRecognition,
            getFeature: (feature: Features) =>
                feature.vectorVendorRecognitionEnabled,
            text: t(
                "tenantConfig.featureConfigurations.vectorVendorRecognitionEnabled.title"
            ),
            helpText: t(
                "tenantConfig.featureConfigurations.vectorVendorRecognitionEnabled.description"
            ),
            value: "vectorVendorRecognitionEnabled",
            checked: features?.vectorVendorRecognitionEnabled,
            isDisabled: false,
            isAvailableForAx: true,
            group: TenantFeatureGroup.vendorSimilaritySearch,
        },
    ];

    const allWorkflows = [
        enableSftpToSettingMapping,
        ...workFlowTypeToSettingMapping,
    ];

    const handleTenantFeatureChange = (
        name: keyof Features,
        value: string | number | boolean
    ) => {
        if (features) {
            setFeatures({
                ...features,
                [name]: value,
            });
        }
        setIsUnsavedChangeAvailable(true);
    };

    const handleOcrConfigurationChange = (
        name: keyof OcrConfiguration,
        value: OcrMethods
    ) => {
        if (ocrConfiguration) {
            setOcrConfiguration({
                ...ocrConfiguration,
                [name]: value,
            });
        }
        setIsUnsavedChangeAvailable(true);
    };

    const handleInvoiceAdditionalHeaderInformation = (
        name: keyof IInvoiceAdditionalHeaderInformation,
        value: string
    ) => {
        if (workflowFromXmlField) {
            setWorkflowFromXmlField({
                ...workflowFromXmlField,
                [name]: value,
            });
        }
        setIsUnsavedChangeAvailable(true);
    };

    const fetchData = () => {
        setDataFetched(false);
        showLoader(true);
        const promises: any[] = [];
        promises.push(
            getTenantFeatureSettingWorkFlows(
                envMoniker,
                tenantMoniker,
                cancelTokenSource
            )
                .then(([workFlows]) => {
                    setFeatureSettingWorkFlows(workFlows || []);
                })
                .catch((error: Error) => {
                    const axiosError = error;
                    if (!Axios.isCancel(axiosError)) {
                        setFeaturesErrorMessage(error.message);
                    }
                }),
            loadFeatureConfigurations(envMoniker, tenantId, cancelTokenSource)
                .then(([payload, status]) => {
                    if (status === constants.statusCode.OK && payload) {
                        originalFeatures.current = payload;
                        setFeatures(payload);
                    } else {
                        const errorMessage = getErrorMessage(
                            String(payload),
                            status
                        );
                        setFeaturesErrorMessage(errorMessage);
                    }
                })
                .catch((error: Error) => {
                    const axiosError = error;
                    if (!Axios.isCancel(axiosError)) {
                        setFeaturesErrorMessage(error.message);
                    }
                }),
            loadOcrConfigurations(environmentId, tenantId, cancelTokenSource)
                .then(([payload, status]) => {
                    if (status === constants.statusCode.OK && payload) {
                        originalOcrConfiguration.current = payload;
                        setOcrConfiguration(payload);
                    } else {
                        const errorMessage = getErrorMessage(
                            String(payload),
                            status
                        );
                        setFeaturesErrorMessage(errorMessage);
                    }
                })
                .catch((error: Error) => {
                    const axiosError = error;
                    if (!Axios.isCancel(axiosError)) {
                        setFeaturesErrorMessage(error.message);
                    }
                }),
            loadConfluenceDocumentLinks(cancelTokenSource)
                .then(([payload, status]) => {
                    if (status === constants.statusCode.OK && payload) {
                        setConfluenceDocumentLinks(payload);
                    } else {
                        const errorMessage = getErrorMessage(
                            String(payload),
                            status
                        );
                        toaster.push(
                            <Message type="error" showIcon>
                                {errorMessage}
                            </Message>,
                            { duration: constants.errorAlertDurationMsec }
                        );
                    }
                })
                .catch((error: Error) => {
                    const axiosError = error;
                    if (!Axios.isCancel(axiosError)) {
                        setFeaturesErrorMessage(error.message);
                    }
                }),
            loadMicrosoftDocumentLinks(cancelTokenSource)
                .then(([payload, status]) => {
                    if (status === constants.statusCode.OK && payload) {
                        setMicrosoftDocumentLinks(payload);
                    } else {
                        const errorMessage = getErrorMessage(
                            String(payload),
                            status
                        );
                        toaster.push(
                            <Message type="error" showIcon>
                                {errorMessage}
                            </Message>,
                            { duration: constants.errorAlertDurationMsec }
                        );
                    }
                })
                .catch((error: Error) => {
                    const axiosError = error;
                    if (!Axios.isCancel(axiosError)) {
                        setFeaturesErrorMessage(error.message);
                    }
                }),
            loadSftpUserIpAddresses(envMoniker, tenantId, cancelTokenSource)
                .then(([payload, status]) => {
                    if (status === constants.statusCode.OK) {
                        originalSftpUserWithIpAddresses.current = payload;
                        setSftpUserWithIpAddresses(payload);
                    } else if (status === constants.statusCode.NoContent) {
                        originalSftpUserWithIpAddresses.current =
                            defaultSftpUser;
                        setSftpUserWithIpAddresses(defaultSftpUser);
                    } else {
                        const errorMessage = getErrorMessage(
                            String(payload),
                            status
                        );
                        toaster.push(
                            <Message type="error" showIcon>
                                {errorMessage}
                            </Message>,
                            { duration: constants.errorAlertDurationMsec }
                        );
                    }
                })
                .catch((error: Error) => {
                    const axiosError = error;
                    if (!Axios.isCancel(axiosError)) {
                        setFeaturesErrorMessage(error.message);
                    }
                }),
            appSettings.isAlertRuleEstablishmentEnabled &&
                checkTenantDatabaseAlertRuleExistence(
                    envMoniker,
                    tenantMoniker,
                    cancelTokenSource
                )
                    .then(([payload, status]) => {
                        if (status === constants.statusCode.OK) {
                            setAlertRuleExists(payload);
                        } else {
                            const errorMessage = getErrorMessage(
                                String(payload),
                                status
                            );
                            setFeaturesErrorMessage(errorMessage);
                        }
                    })
                    .catch((error: Error) => {
                        const axiosError = error;
                        if (!Axios.isCancel(axiosError)) {
                            setFeaturesErrorMessage(error.message);
                        }
                    }),
            loadSearchServices(environmentId, tenantId, cancelTokenSource).then(
                ([payload, status]) => {
                    if (payload && status === constants.statusCode.OK) {
                        setSearchServices(payload);
                    } else {
                        const errorMessage = getErrorMessage(
                            String(payload),
                            status
                        );
                        setFeaturesErrorMessage(errorMessage);
                    }
                }
            ),
            loadSearchServiceLinked(
                environmentId,
                tenantId,
                cancelTokenSource
            ).then(([payload, status]) => {
                if (payload && status === constants.statusCode.OK) {
                    setLinkedSearchService(payload.name);
                    setOriginalSearchService(payload.name);
                } else if (status === constants.statusCode.NotFound) {
                    setLinkedSearchService(null);
                    setOriginalSearchService(null);
                } else {
                    const errorMessage = getErrorMessage(
                        String(payload),
                        status
                    );
                    setFeaturesErrorMessage(errorMessage);
                }
            })
        );
        Promise.all(promises).then(() => {
            showLoader(false);
            setDataFetched(true);
        });
    };

    const isPowerBiWorkspaceDeploymentEnabled =
        appSettings.isPowerBiWorkspaceDeploymentEnabled &&
        isPowerBiWorkspaceDeploymentAllowed;

    const alertRuleDeploymentEnabled =
        appSettings.isAlertRuleEstablishmentEnabled && !alertRuleExists;

    const getNumberValidation = (min: number, max: number) => {
        return Schema.Types.NumberType()
            .isRequired(t("generalValidationMessages.isRequired"))
            .isInteger(t("generalValidationMessages.notInteger"))
            .range(
                min,
                max,
                template(t("generalValidationMessages.numberRange"))({
                    min,
                    max,
                })
            );
    };

    const getStringValidation = () =>
        Schema.Types.StringType()
            .maxLength(
                constants.maxCustomFieldLimit,
                t("tenantConfig.customFieldsPage.validation.maxLength")
            )
            .addRule(
                (value) => validateContent(value, constants.xmlSelectorRegex),
                t("tenantConfig.customFieldsPage.validation.letters")
            );

    const formModel = Schema.Model({
        xPath: getStringValidation(),
        autoTTPMaxBatchSize: getNumberValidation(
            constants.ranges.minRangeValue1,
            constants.ranges.dotnetInt32MaxValue
        ),
        userSessionIdleTimeoutInMinutes: getNumberValidation(
            constants.ranges.minRangeValue1,
            constants.ranges.dotnetInt32MaxValue
        ),
        workflowPredictionImportMinScore: getNumberValidation(
            constants.ranges.minRangeValue0,
            constants.ranges.maxRangeValue100
        ),
        companyPredictionImportMinScore: getNumberValidation(
            constants.ranges.minRangeValue0,
            constants.ranges.maxRangeValue100
        ),
        codingPredictionImportMinScore: getNumberValidation(
            constants.ranges.minRangeValue0,
            constants.ranges.maxRangeValue100
        ),
        vendorPredictionVendorAddressMinScore: getNumberValidation(
            constants.ranges.minRangeValue0,
            constants.ranges.maxRangeValue100
        ),
        vendorPredictionVendorNameMinScore: getNumberValidation(
            constants.ranges.minRangeValue0,
            constants.ranges.maxRangeValue100
        ),
        vectorVendorRecognitionMinScore: getNumberValidation(
            constants.ranges.minRangeValue0,
            constants.ranges.maxRangeValue100
        ),
    });

    const handleResourceDeployClick = (
        workflowType: TenantFeatureSettingWorkFlowType
    ) => {
        const settingDefinition = tenantFeatureSettingDefinitions[workflowType];

        toggleTenantFeatureSetting(
            envMoniker,
            tenantMoniker,
            settingDefinition.urlParameterValue,
            {
                settingName: settingDefinition.urlParameterValue,
                toggleValue: true,
                data: null,
            }
        ).then((response) => {
            const [, status] = response;
            const isResponseStatusCodeExpected =
                status === constants.statusCode.NoContent ||
                status === constants.statusCode.OK;

            if (isResponseStatusCodeExpected) {
                toaster.push(
                    <Message type="success" showIcon>
                        {template(
                            t(
                                "tenantConfig.featureConfigurations.featureActivationMessages.startedSuccessfully"
                            )
                        )({
                            featureName: settingDefinition.displayName,
                        })}
                    </Message>,
                    {
                        duration: constants.alertDurationMsec,
                    }
                );
            } else {
                toaster.push(
                    <Message type="error" showIcon>
                        {template(
                            t(
                                "tenantConfig.featureConfigurations.featureActivationMessages.startedFailure"
                            )
                        )({
                            featureName: settingDefinition.displayName,
                        })}
                    </Message>,
                    {
                        duration: constants.errorAlertDurationMsec,
                    }
                );
            }
        });
    };

    const handleRecreateModalConfirm = () => {
        setModifySearchDisabled(true);

        recreateSearchIndex(environmentId, tenantId).then((response) => {
            const [, status] = response;
            const isResponseStatusCodeExpected =
                status === constants.statusCode.NoContent ||
                status === constants.statusCode.OK;

            if (isResponseStatusCodeExpected) {
                toaster.push(
                    <Message type="success" showIcon>
                        {t(
                            "tenantConfig.featureConfigurations.recreateSearchIndexResult.success"
                        )}
                    </Message>,
                    { duration: constants.alertDurationMsec }
                );
            } else {
                toaster.push(
                    <Message type="error" showIcon>
                        {t(
                            "tenantConfig.featureConfigurations.recreateSearchIndexResult.failure"
                        )}
                    </Message>,
                    { duration: constants.errorAlertDurationMsec }
                );
            }
            setModifySearchDisabled(false);
        });
        setSearchConfirmVisible(false);
    };

    const updateTenantFeatureSettings = async () => {
        if (
            featureSettingWorkFlows !== null &&
            features &&
            envMoniker &&
            tenantMoniker
        ) {
            const settingsToUpdate: TenantFeatureSettingWorkFlowToggleValue[] =
                [];
            workFlowTypeToSettingMapping.forEach(
                ({ workflowType, getFeature }) => {
                    const newSettingValue = getFeature(features);
                    if (
                        originalFeatures.current === null ||
                        getFeature(originalFeatures.current) !== newSettingValue
                    ) {
                        const settingDefinition =
                            tenantFeatureSettingDefinitions[workflowType] ??
                            null;
                        if (
                            settingDefinition !== null &&
                            isTenantFeatureSettingWithTypeEnabled(
                                featureSettingWorkFlows,
                                workflowType
                            )
                        ) {
                            settingsToUpdate.push({
                                settingName:
                                    settingDefinition.urlParameterValue,
                                toggleValue: newSettingValue,
                                data: null,
                            });
                        }
                    }
                }
            );
            if (settingsToUpdate.length !== 0) {
                addDisabledSettingNames(
                    settingsToUpdate.map((setting) => setting.settingName)
                );
                await toggleTenantFeatureSettings(
                    envMoniker,
                    tenantMoniker,
                    settingsToUpdate
                );
                originalFeatures.current = cloneDeep(features);
            }
        }
    };

    const updateTenantEnableSftp = async () => {
        if (
            enableSftpWorkflow !== null &&
            features &&
            envMoniker &&
            tenantMoniker
        ) {
            const newSettingValue =
                enableSftpToSettingMapping.getFeature(features);

            if (
                originalFeatures.current === null ||
                enableSftpToSettingMapping.getFeature(
                    originalFeatures.current
                ) !== newSettingValue
            ) {
                const settingDefinition =
                    tenantFeatureSettingDefinitions[
                        enableSftpToSettingMapping.workflowType
                    ] ?? null;
                if (
                    settingDefinition !== null &&
                    isTenantFeatureSettingWithTypeEnabled(
                        featureSettingWorkFlows,
                        enableSftpToSettingMapping.workflowType
                    )
                ) {
                    const settingToUpdate = {
                        settingName: settingDefinition.urlParameterValue,
                        toggleValue: newSettingValue,
                        data: sftpUserWithIpAddresses?.allowedIpAddresses || [],
                    };

                    addDisabledSettingNames([settingToUpdate.settingName]);

                    await toggleTenantFeatureSetting(
                        envMoniker,
                        tenantMoniker,
                        settingToUpdate.settingName,
                        settingToUpdate
                    );

                    originalFeatures.current = cloneDeep(features);
                }
            }
        }
    };

    const updateSearchServiceSelection = async () => {
        if (
            searchServiceSelectionWorkflow !== null &&
            envMoniker &&
            tenantMoniker
        ) {
            if (linkedSearchService !== originalSearchService) {
                const settingDefinition =
                    tenantFeatureSettingDefinitions[
                        TenantFeatureSettingWorkFlowType.SearchServiceSelection
                    ] ?? null;
                if (
                    settingDefinition !== null &&
                    isTenantFeatureSettingWithTypeEnabled(
                        featureSettingWorkFlows,
                        TenantFeatureSettingWorkFlowType.SearchServiceSelection
                    )
                ) {
                    const searchServiceToSelect = searchServices.find(
                        (s) => s.name === linkedSearchService
                    );
                    if (searchServiceToSelect) {
                        const settingToUpdate = {
                            settingName: settingDefinition.urlParameterValue,
                            toggleValue: true,
                            data: searchServiceToSelect,
                        };

                        await toggleTenantFeatureSetting(
                            envMoniker,
                            tenantMoniker,
                            settingToUpdate.settingName,
                            settingToUpdate
                        );
                    }
                }
            }
        }
    };

    const handleUpdateClick = () => {
        setModifySearchDisabled(true);

        updateSearchIndex(environmentId, tenantId).then((response) => {
            const [, status] = response;
            const isResponseStatusCodeExpected =
                status === constants.statusCode.NoContent ||
                status === constants.statusCode.OK;

            if (isResponseStatusCodeExpected) {
                toaster.push(
                    <Message type="success" showIcon>
                        {t(
                            "tenantConfig.featureConfigurations.updateSearchIndexResult.success"
                        )}
                    </Message>,
                    { duration: constants.alertDurationMsec }
                );
            } else {
                toaster.push(
                    <Message type="error" showIcon>
                        {t(
                            "tenantConfig.featureConfigurations.updateSearchIndexResult.failure"
                        )}
                    </Message>,
                    { duration: constants.errorAlertDurationMsec }
                );
            }
            setModifySearchDisabled(false);
        });
    };

    const handleSeedClick = () => {
        setModifySearchDisabled(true);

        seedVectorizationData(environmentId, tenantId).then((response) => {
            const [, status] = response;
            const isResponseStatusCodeExpected =
                status === constants.statusCode.NoContent ||
                status === constants.statusCode.OK;

            if (isResponseStatusCodeExpected) {
                toaster.push(
                    <Message type="success" showIcon>
                        {t(
                            "tenantConfig.featureConfigurations.seedVectorizationDataResult.success"
                        )}
                    </Message>,
                    { duration: constants.alertDurationMsec }
                );
            } else {
                toaster.push(
                    <Message type="error" showIcon>
                        {t(
                            "tenantConfig.featureConfigurations.seedVectorizationDataResult.failure"
                        )}
                    </Message>,
                    { duration: constants.errorAlertDurationMsec }
                );
            }
            setModifySearchDisabled(false);
        });
    };

    const handleReindexClick = () => {
        setModifySearchDisabled(true);

        reIndexVectorizationData(environmentId, tenantId).then((response) => {
            const [, status] = response;
            const isResponseStatusCodeExpected =
                status === constants.statusCode.NoContent ||
                status === constants.statusCode.OK;

            if (isResponseStatusCodeExpected) {
                toaster.push(
                    <Message type="success" showIcon>
                        {t(
                            "tenantConfig.featureConfigurations.reIndexVectorizationDataResult.success"
                        )}
                    </Message>,
                    { duration: constants.alertDurationMsec }
                );
            } else {
                toaster.push(
                    <Message type="error" showIcon>
                        {t(
                            "tenantConfig.featureConfigurations.reIndexVectorizationDataResult.failure"
                        )}
                    </Message>,
                    { duration: constants.errorAlertDurationMsec }
                );
            }
            setModifySearchDisabled(false);
        });
    };

    const checkWorkflowProgressAndUpdateWorkflow = (
        settingWorkflow: ITenantFeatureSettingWorkFlow
    ) => {
        setFeatureSettingWorkFlows((featureSettingWorkFlows) => {
            return updateTenantSettingWorkFlow(
                featureSettingWorkFlows,
                settingWorkflow
            );
        });
        if (
            settingWorkflow.type ===
                TenantFeatureSettingWorkFlowType.DocumentsTransfer ||
            settingWorkflow.type ===
                TenantFeatureSettingWorkFlowType.CodingPrediction ||
            settingWorkflow.type ===
                TenantFeatureSettingWorkFlowType.SalesTaxPrediction ||
            settingWorkflow.type ===
                TenantFeatureSettingWorkFlowType.WorkflowPrediction ||
            settingWorkflow.type ===
                TenantFeatureSettingWorkFlowType.D365RoleDooapRequiredToAccessDooap ||
            settingWorkflow.type ===
                TenantFeatureSettingWorkFlowType.EnableSftp ||
            settingWorkflow.type ===
                TenantFeatureSettingWorkFlowType.SearchServiceSelection ||
            settingWorkflow.type ===
                TenantFeatureSettingWorkFlowType.SimilarityVendorRecognition
        ) {
            if (isEveryWorkflowStepCompleted(settingWorkflow.steps)) {
                fetchData();
            }
        }
    };

    const updateTenantConfigurations = async () => {
        if (features && originalFeatures.current) {
            const featuresWithoutNumericStrings =
                convertNumericStringProperties(features);
            const originalFeaturesWithoutNumericStrings =
                convertNumericStringProperties(originalFeatures.current);
            if (
                !isEqual(
                    featuresWithoutNumericStrings,
                    originalFeaturesWithoutNumericStrings
                )
            ) {
                const [payload, status] = await updateFeatureConfigurations(
                    envMoniker,
                    tenantId,
                    features
                );

                if (status !== constants.statusCode.OK) {
                    const errorMessage = getErrorMessage(
                        String(payload),
                        status
                    );
                    toaster.push(
                        <Message showIcon type="error">
                            {errorMessage}
                        </Message>,
                        {
                            duration: constants.errorAlertDurationMsec,
                        }
                    );
                } else {
                    originalFeatures.current = cloneDeep(features);
                }
            }
        }
    };

    const updateOcrMethod = async () => {
        if (
            ocrConfiguration?.ocrMethod &&
            !isEqual(originalOcrConfiguration.current, ocrConfiguration)
        ) {
            const [payload, status] = await updateOcrConfigurations(
                environmentId,
                tenantId,
                ocrConfiguration
            );

            if (status !== constants.statusCode.OK) {
                const errorMessage = getErrorMessage(String(payload), status);
                toaster.push(
                    <Message showIcon type="error">
                        {errorMessage}
                    </Message>,
                    {
                        duration: constants.errorAlertDurationMsec,
                    }
                );
            } else {
                originalOcrConfiguration.current = cloneDeep(ocrConfiguration);
            }
        }
    };

    const updateWorkflowFromXmlField = async () => {
        if (
            features?.workflowFromXml &&
            workflowFromXmlField?.xPath !== null &&
            !isEqual(originalWorkflowFromXmlField.current, workflowFromXmlField)
        ) {
            const [payload, status] =
                await updateInvoiceAdditionalHeaderInformationField(
                    envMoniker,
                    tenantId,
                    workflowFromXmlField!
                );
            if (status !== constants.statusCode.OK) {
                const errorMessage = getErrorMessage(String(payload), status);
                toaster.push(
                    <Message showIcon type="error">
                        {errorMessage}
                    </Message>,
                    {
                        duration: constants.errorAlertDurationMsec,
                    }
                );
            } else {
                originalWorkflowFromXmlField.current =
                    cloneDeep(workflowFromXmlField);
            }
        }
    };

    const updateTenantSftpUserIpAddresses = async () => {
        const nullifyEditing = (
            sftpUserWithIpAddresses: ISftpUserWithIpAddresses
        ): ISftpUserWithIpAddresses => {
            return {
                ...sftpUserWithIpAddresses,
                allowedIpAddresses:
                    sftpUserWithIpAddresses.allowedIpAddresses.map(
                        (ipAddress: IAllowedIpAddress) => {
                            return { ...ipAddress, editing: null };
                        }
                    ),
            };
        };
        const sftpUserWithoutEditing = nullifyEditing(sftpUserWithIpAddresses);
        const originalSftpUserWithoutEditing = nullifyEditing(
            originalSftpUserWithIpAddresses.current
        );

        if (
            originalFeatures.current?.sftpEnabled &&
            features?.sftpEnabled &&
            !isEqual(sftpUserWithoutEditing, originalSftpUserWithoutEditing)
        ) {
            const [payload, status] = await updateSftpUserIpAddresses(
                envMoniker,
                tenantId,
                sftpUserWithIpAddresses
            );

            if (status !== constants.statusCode.NoContent) {
                const errorMessage = getErrorMessage(String(payload), status);
                toaster.push(
                    <Message showIcon type="error">
                        {errorMessage}
                    </Message>,
                    {
                        duration: constants.errorAlertDurationMsec,
                    }
                );
            } else {
                originalSftpUserWithIpAddresses.current = cloneDeep(
                    sftpUserWithIpAddresses
                );
            }
        }
    };

    const handleRecreateModalCancel = () => {
        setSearchConfirmVisible(false);
    };

    const handleRecreateClick = () => {
        setSearchConfirmVisible(true);
    };

    const handleLogoUpdate = async (file: File) => {
        const name = `${logoName}${getFileExtension(file)}`;

        const [payload, status] = await uploadLogo(
            environmentId,
            tenantId,
            name,
            file
        );

        if (
            status === constants.statusCode.OK ||
            status === constants.statusCode.NoContent
        ) {
            setFeatures((draft) => {
                if (draft?.companyLogoUrl) {
                    draft.companyLogoUrl = setFileName(
                        new URL(draft.companyLogoUrl),
                        name
                    ).toString();
                }
            });

            const successMessage = t(
                "tenantConfig.featureConfigurations.logoSection.successMessage"
            );

            toaster.push(
                <Message showIcon type="success">
                    {successMessage}
                </Message>,
                {
                    duration: constants.alertDurationMsec,
                }
            );
        } else {
            const errorMessage = getErrorMessage(String(payload), status);
            toaster.push(
                <Message showIcon type="error">
                    {errorMessage}
                </Message>,
                {
                    duration: constants.errorAlertDurationMsec,
                }
            );
        }
    };

    const handleFormSubmit = async () => {
        if (!isSettingsFormValid || !areSftpAddressesValid) {
            toaster.push(
                <Message showIcon type="error">
                    {t("tenantConfig.generalConfigurationPage.warning")}
                </Message>,
                {
                    duration: constants.errorAlertDurationMsec,
                }
            );

            return;
        }
        showLoader(true);

        await updateAllTenantConfigurations();
        setIsUnsavedChangeAvailable(false);
    };

    const updateAllTenantConfigurations = async () => {
        const promises: any[] = [];
        promises.push(
            updateTenantConfigurations(),
            updateTenantFeatureSettings(),
            updateTenantEnableSftp(),
            updateOcrMethod(),
            updateSearchServiceSelection(),
            updateWorkflowFromXmlField(),
            updateTenantSftpUserIpAddresses()
        );

        Promise.all(promises).then(() => {
            showLoader(false);
            toaster.push(
                <Message showIcon type="success">
                    {t("tenantConfig.common.successfulUpdateAlertMessage")}
                </Message>,
                {
                    duration: constants.alertDurationMsec,
                }
            );
        });
    };

    useEffect(() => {
        if (tenantName && envMoniker) {
            const sanitizedTenantName = tenantName
                .replace(constants.fileNameInvalidCharsRegex, "")
                .toLowerCase();

            setLogoName(`${sanitizedTenantName}_${envMoniker}`);
        }
    }, [tenantName, envMoniker]);

    useRefreshPage(() => {
        if (envMoniker && tenantMoniker) {
            setDataFetched(!isDataFetched);
        }
    });

    useEffect(() => {
        if (!isDataFetched) {
            fetchData();
        }
    }, [isDataFetched]);

    useEffect(() => {
        if (envMoniker && tenantId && features) {
            loadInvoiceAdditionalHeaderInformationField(
                envMoniker,
                tenantId,
                features.workflowFromXmlFieldName,
                cancelTokenSource
            ).then(([payload, status]) => {
                if (payload && status === constants.statusCode.OK) {
                    originalWorkflowFromXmlField.current = payload;
                    setWorkflowFromXmlField(payload);
                } else {
                    originalWorkflowFromXmlField.current = null;
                    setWorkflowFromXmlField(null);
                }
            });
        }
    }, [envMoniker, tenantId, isDataFetched]);

    useEffect(() => {
        if (
            isEverySettingWorkFlowUpdated(
                previousFeatureSettingWorkFlows,
                featureSettingWorkFlows
            )
        ) {
            toaster.push(
                <Message showIcon type="success">
                    {t("tenantConfig.common.featureSettingsUpdateAlertMessage")}
                </Message>,
                {
                    duration: constants.alertDurationMsec,
                }
            );
        }
    }, [previousFeatureSettingWorkFlows, featureSettingWorkFlows]);

    useEffect(() => {
        if (formRef && formRef.current) {
            if (formRef.current.check()) {
                setSettingsFormValid(true);
                formRef.current.cleanErrors();
            } else {
                setSettingsFormValid(false);
            }
        }
    }, [formRef, features, setSettingsFormValid]);

    // According to the requirement we need to turn off sales tax prediction when coding prediction is off
    useEffect(() => {
        if (
            !features?.codingPredictionEnabled &&
            features?.salesTaxPredictionEnabled
        ) {
            handleTenantFeatureChange("salesTaxPredictionEnabled", false);
        }
    }, [features?.codingPredictionEnabled]);

    if (featuresErrorMessage) {
        return (
            <div className={"pageContainer"}>
                <ErrorSection errorMessage={featuresErrorMessage} />
            </div>
        );
    }

    return (
        (isDataFetched && features && ocrConfiguration && (
            <div className="feature-configurations__container">
                <div className="feature-configurations__content">
                    <SignalRTenantFeatureSetting
                        tenantId={tenantId}
                        updateTenantFeatureSettingWorkFlows={(
                            settingWorkflow: ITenantFeatureSettingWorkFlow
                        ) => {
                            removeDisabledSettingName(
                                settingWorkflow.featureName
                            );
                            checkWorkflowProgressAndUpdateWorkflow(
                                settingWorkflow
                            );
                        }}
                    >
                        <ConfirmModal
                            onCancel={handleRecreateModalCancel}
                            onConfirm={handleRecreateModalConfirm}
                            open={searchConfirmVisible}
                            message={t(
                                "tenantConfig.featureConfigurations.recreateSearchIndexConfirmMessage"
                            )}
                        />

                        <Heading level={1}>{title}</Heading>

                        <Heading
                            level={2}
                            className={
                                "feature-configurations__page-sub-header"
                            }
                        >
                            {t(
                                "tenantConfig.featureConfigurations.settingsHeader"
                            )}
                        </Heading>
                        <Heading
                            level={4}
                            className={
                                "feature-configurations__header-description"
                            }
                        >
                            {t(
                                "tenantConfig.generalConfigurationPage.settingsSubHeaderText"
                            )}
                            <a
                                className={
                                    "feature-configurations__header-link"
                                }
                                href={
                                    confluenceDocumentLinks?.featuresAndSettingsOverview
                                }
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                {t(
                                    "tenantConfig.generalConfigurationPage.settingsSubHeaderLink"
                                )}
                            </a>
                        </Heading>

                        {alertRuleDeploymentEnabled && (
                            <Message
                                className={
                                    "feature-configurations__warning-message"
                                }
                                showIcon
                                type="warning"
                            >
                                {t(
                                    "tenantConfig.featureConfigurations.databaseAlertRule.warningMessage"
                                )}
                            </Message>
                        )}

                        <Form
                            ref={formRef}
                            model={formModel}
                            formValue={{
                                ...features,
                                xPath: workflowFromXmlField?.xPath,
                            }}
                        >
                            <FeatureGroups
                                features={features}
                                ocrConfiguration={ocrConfiguration}
                                confluenceDocumentLinks={
                                    confluenceDocumentLinks
                                }
                                microsoftDocumentLinks={microsoftDocumentLinks}
                                customerType={customerType}
                                featureSettingWorkFlows={
                                    featureSettingWorkFlows
                                }
                                disabledSettingNames={disabledSettingNames}
                                allWorkflows={allWorkflows}
                                sftpUserWithIpAddresses={
                                    sftpUserWithIpAddresses
                                }
                                areSftpAddressesValid={areSftpAddressesValid}
                                workflowFromXmlField={workflowFromXmlField}
                                isModifySearchDisabled={isModifySearchDisabled}
                                searchServices={searchServices}
                                linkedSearchService={linkedSearchService}
                                setSftpUserWithIpAddresses={
                                    setSftpUserWithIpAddresses
                                }
                                setSftpAddressesValid={setSftpAddressesValid}
                                handleTenantFeatureChange={
                                    handleTenantFeatureChange
                                }
                                handleOcrConfigurationChange={
                                    handleOcrConfigurationChange
                                }
                                handleInvoiceAdditionalHeaderInformation={
                                    handleInvoiceAdditionalHeaderInformation
                                }
                                handleUpdateClick={handleUpdateClick}
                                handleRecreateClick={handleRecreateClick}
                                handleReindexClick={handleReindexClick}
                                handleSeedClick={handleSeedClick}
                                setLinkedSearchService={setLinkedSearchService}
                                setIsUnsavedChangeAvailable={
                                    setIsUnsavedChangeAvailable
                                }
                                setDataFetched={setDataFetched}
                            />
                            {isPowerBiWorkspaceDeploymentEnabled && (
                                <>
                                    <Divider />
                                    <Form.Group
                                        className={cn(
                                            "general-configurations__grid-item"
                                        )}
                                    >
                                        <Form.ControlLabel
                                            className={
                                                "general-configurations__label"
                                            }
                                        >
                                            {t(
                                                "tenantConfig.featureConfigurations.powerBiWorkspace.title"
                                            )}
                                            <Form.HelpText
                                                className={
                                                    "general-configurations__help"
                                                }
                                                tooltip
                                            >
                                                {t(
                                                    "tenantConfig.featureConfigurations.powerBiWorkspace.tooltip"
                                                )}
                                            </Form.HelpText>
                                        </Form.ControlLabel>
                                        <div
                                            className={
                                                "feature-configurations__section"
                                            }
                                        >
                                            <div
                                                className={
                                                    "feature-configurations__modify-search-index-section"
                                                }
                                            >
                                                <Form.Control
                                                    name={"power-bi"}
                                                    disabled={
                                                        !!powerBiWorkspaceDeploymentWorkflow?.id
                                                    }
                                                    accepter={Button}
                                                    className={cn(
                                                        "feature-configurations__general-btn",
                                                        "feature-configurations__power-bi-btn"
                                                    )}
                                                    onClick={() => {
                                                        handleResourceDeployClick(
                                                            TenantFeatureSettingWorkFlowType.PowerBiWorkspaceDeployment
                                                        );
                                                    }}
                                                >
                                                    {t(
                                                        "tenantConfig.featureConfigurations.powerBiWorkspace.createButton"
                                                    )}
                                                </Form.Control>
                                                {powerBiWorkspaceDeploymentWorkflow && (
                                                    <ProcessModalButton
                                                        className={
                                                            "feature-configurations__less-height"
                                                        }
                                                        workflow={
                                                            powerBiWorkspaceDeploymentWorkflow
                                                        }
                                                        workflowType={
                                                            WorkflowModalType.Feature
                                                        }
                                                        triggerRefresh={() => {
                                                            setDataFetched(
                                                                false
                                                            );
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </Form.Group>
                                </>
                            )}
                            {alertRuleDeploymentEnabled && (
                                <>
                                    <Divider />
                                    <Form.Group
                                        className={cn(
                                            "general-configurations__grid-item"
                                        )}
                                    >
                                        <Form.ControlLabel
                                            className={
                                                "general-configurations__label"
                                            }
                                        >
                                            {t(
                                                "tenantConfig.featureConfigurations.databaseAlertRule.title"
                                            )}
                                            <Form.HelpText
                                                className={
                                                    "general-configurations__help"
                                                }
                                                tooltip
                                            >
                                                {t(
                                                    "tenantConfig.featureConfigurations.databaseAlertRule.tooltip"
                                                )}
                                            </Form.HelpText>
                                        </Form.ControlLabel>
                                        <div
                                            className={
                                                "feature-configurations__section"
                                            }
                                        >
                                            <div
                                                className={
                                                    "feature-configurations__modify-search-index-section"
                                                }
                                            >
                                                <Form.Control
                                                    name={"power-bi"}
                                                    disabled={
                                                        !!alertRuleDeploymentWorkflow?.id
                                                    }
                                                    accepter={Button}
                                                    className={cn(
                                                        "feature-configurations__general-btn",
                                                        "feature-configurations__power-bi-btn"
                                                    )}
                                                    onClick={() => {
                                                        handleResourceDeployClick(
                                                            TenantFeatureSettingWorkFlowType.DatabaseAlertRuleCreation
                                                        );
                                                    }}
                                                >
                                                    {t(
                                                        "tenantConfig.featureConfigurations.databaseAlertRule.createButton"
                                                    )}
                                                </Form.Control>
                                                {alertRuleDeploymentWorkflow && (
                                                    <ProcessModalButton
                                                        className={
                                                            "feature-configurations__less-height"
                                                        }
                                                        workflow={
                                                            alertRuleDeploymentWorkflow
                                                        }
                                                        workflowType={
                                                            WorkflowModalType.Feature
                                                        }
                                                        triggerRefresh={() => {
                                                            setDataFetched(
                                                                false
                                                            );
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </Form.Group>
                                </>
                            )}
                            <div
                                className={cn(
                                    "feature-configurations__subsection-header",
                                    "general-configurations__info-msg"
                                )}
                            >
                                {t(
                                    "tenantConfig.featureConfigurations.tenantFeatureSettings.label"
                                )}
                            </div>
                            <Form.Group
                                className={cn(
                                    "general-configurations__grid-item",
                                    "feature-configurations__less-height"
                                )}
                            >
                                <Checkbox
                                    className={
                                        "feature-configurations__checkbox"
                                    }
                                    value={"vendorPrepaymentRegisterCheck"}
                                    checked={
                                        features.vendorPrepaymentRegisterCheck
                                    }
                                    disabled={featureSettingWorkFlows === null}
                                    onChange={(_, cheked) =>
                                        handleTenantFeatureChange(
                                            "vendorPrepaymentRegisterCheck" as keyof Features,
                                            cheked
                                        )
                                    }
                                >
                                    <div>
                                        {t(
                                            "tenantConfig.featureConfigurations.vendorPrepaymentRegisterCheck.title"
                                        )}
                                        <Form.HelpText
                                            tooltip
                                            className={
                                                "feature-configurations__help-block"
                                            }
                                        >
                                            {t(
                                                "tenantConfig.featureConfigurations.vendorPrepaymentRegisterCheck.description"
                                            )}
                                        </Form.HelpText>
                                    </div>
                                </Checkbox>
                            </Form.Group>
                        </Form>

                        <Logo
                            action={""}
                            defaultName={logoName}
                            src={features.companyLogoUrl}
                            onUpdate={(file) =>
                                handleLogoUpdate(file.blobFile!)
                            }
                        />
                    </SignalRTenantFeatureSetting>
                </div>
                <Footer onSubmit={handleFormSubmit} />
            </div>
        )) ||
        null
    );
};

export default FeatureConfigurations;
